import { siteInfo } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';

const brandValues = [
  {
    icon: 'ri-shield-check-line',
    title: '正版授权',
    description: '始终坚持正版授权原则，所有内容均经过创作者授权后发布，保障创作者权益，为用户提供安全合法的优质内容。',
  },
  {
    icon: 'ri-palette-line',
    title: '品质为先',
    description: '严格的内容审核机制，确保平台上每一篇作品都达到高质量标准。我们相信，好内容值得被更多人看见。',
  },
  {
    icon: 'ri-heart-line',
    title: '热爱驱动',
    description: '萝幼社的团队和创作者都怀着对二次元文化的真挚热爱。这份热爱驱使我们不断进步，创造更好的体验。',
  },
  {
    icon: 'ri-group-line',
    title: '社区共创',
    description: '我们坚信最好的内容来自社区。通过为创作者提供良好的创作环境和激励机制，让灵感自由生长。',
  },
];

const milestones = [
  { year: '2019', event: '萝幼社正式成立，以插画分享社区起步' },
  { year: '2020', event: '平台注册用户突破 10 万，拓展漫画和同人分类' },
  { year: '2021', event: '获得天使轮融资，团队扩展至 50 人' },
  { year: '2022', event: '推出创作者激励计划，认证创作者突破 1,000 人' },
  { year: '2023', event: '新增 Cosplay 和声优配音板块，注册用户突破 30 万' },
  { year: '2024', event: '获得 A 轮融资，全面升级平台技术和内容生态' },
  { year: '2025', event: '注册用户突破 50 万，成为国内领先的二次元内容平台' },
  { year: '2026', event: '推出动画短片板块，持续深化内容生态建设' },
];

const testimonials = [
  {
    name: '樱月画师',
    role: '插画创作者',
    content: '在萝幼社发布作品以来，我收获了超乎想象的支持和反馈。平台对创作者的尊重和保护让我能够专注于创作本身。',
    avatar: 'https://readdy.ai/api/search-image?query=Cute%20anime%20style%20female%20avatar%20with%20pink%20hair%2C%20warm%20smile%2C%20soft%20pastel%20colors%2C%20clean%20white%20background&width=80&height=80&seq=testimonial-01&orientation=squarish',
  },
  {
    name: '星河工作室',
    role: '漫画创作团队',
    content: '《星河纪元》从默默无闻到拥有数万忠实读者，萝幼社的平台推广和读者互动机制功不可没。这里的读者质量真的很高。',
    avatar: 'https://readdy.ai/api/search-image?query=Cool%20anime%20style%20studio%20avatar%20with%20stars%2C%20professional%20look%2C%20dark%20theme%2C%20clean%20background&width=80&height=80&seq=testimonial-02&orientation=squarish',
  },
  {
    name: '光影师阿杰',
    role: 'Cos 摄影师',
    content: '萝幼社的 Cos 板块氛围特别好，大家互相交流摄影技巧和道具制作经验，真的像一个温暖的大家庭。',
    avatar: 'https://readdy.ai/api/search-image?query=Cool%20anime%20style%20male%20photographer%20avatar%2C%20dark%20hair%2C%20friendly%20expression%2C%20clean%20white%20background&width=80&height=80&seq=testimonial-03&orientation=squarish',
  },
  {
    name: '二次元爱好者小美',
    role: '平台用户',
    content: '自从发现了萝幼社，我每天都会上来刷一刷。这里的作品质量真的很高，而且分类清晰，总能找到我喜欢的类型。',
    avatar: 'https://readdy.ai/api/search-image?query=Cute%20anime%20style%20girl%20avatar%20with%20headphones%2C%20cheerful%20expression%2C%20colorful%20style%2C%20clean%20white%20background&width=80&height=80&seq=testimonial-04&orientation=squarish',
  },
];

export default function AboutPage() {
  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteInfo.url}/about` },
        ],
      },
      {
        '@type': 'Organization',
        '@id': `${siteInfo.url}/about#organization`,
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        foundingDate: '2019',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '张江高科技园区动漫产业园 A 座 12 层',
          addressLocality: '上海',
          addressRegion: '浦东新区',
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'AboutPage',
        '@id': `${siteInfo.url}/about#webpage`,
        url: `${siteInfo.url}/about`,
        name: `关于我们 - ${siteInfo.name}`,
        description: `了解${siteInfo.name}的品牌故事、核心价值观和联系方式。`,
        isPartOf: { '@id': `${siteInfo.url}/#website` },
        about: { '@id': `${siteInfo.url}/about#organization` },
      },
    ],
  };

  return (
    <>
      <title>关于我们 - 萝幼社 | 官方正版授权二次元内容平台</title>
      <meta name="description" content={`了解${siteInfo.name}的品牌故事、核心价值观和发展历程。${siteInfo.name}是专注于二次元文化的正版授权内容平台。`} />
      <meta name="keywords" content="萝幼社关于我们,萝幼社品牌介绍,萝幼社联系方式,二次元平台介绍" />
      <link rel="canonical" href={`${siteInfo.url}/about`} />
      <meta property="og:title" content={`关于我们 - ${siteInfo.name}`} />
      <meta property="og:description" content={`了解${siteInfo.name}的品牌故事、核心价值观和发展历程。`} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${siteInfo.url}/about`} />
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>

      <main>
        <div className="pt-16">
          <Breadcrumb />

          <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Warm%20and%20creative%20anime%20studio%20workspace%20with%20art%20on%20walls%20and%20drawing%20tablets%2C%20soft%20natural%20lighting%2C%20collaborative%20and%20friendly%20atmosphere%2C%20Japanese%20anime%20aesthetic%2C%20modern%20creative%20office%20with%20plants&width=1600&height=600&seq=about-hero-01&orientation=landscape')`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
            </div>
            <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
              <h1 className="font-heading text-3xl md:text-5xl text-white">关于萝幼社</h1>
              <p className="mt-4 text-sm md:text-base text-white/70 max-w-xl text-center">
                用心守护每一份二次元的热爱
              </p>
            </div>
          </section>

          <section id="brand" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="max-w-3xl mx-auto text-center mb-14">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  品牌故事
                </h2>
                <p className="mt-6 text-sm md:text-base text-foreground-600 leading-relaxed">
                  萝幼社成立于 2019 年，由一群热爱二次元文化的年轻人共同创立。我们的初心很简单：为二次元爱好者打造一个纯粹的、高品质的内容分享平台。
                </p>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  从最初的插画分享社区起步，到如今涵盖插画、漫画、同人创作、Cosplay、声优配音和动画短片六大板块的综合平台，萝幼社始终坚持以正版授权为核心，以品质内容为基础，以社区共创为动力。
                </p>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  八年来，我们见证了无数创作者的成长，也陪伴了千万用户度过了无数美好的二次元时光。未来，萝幼社将继续深耕二次元内容生态，为创作者提供更好的创作环境，为用户带来更丰富的内容体验。
                </p>
              </div>

              <h3 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-10">
                核心价值观
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {brandValues.map((value) => (
                  <div
                    key={value.title}
                    className="p-6 rounded-lg bg-background-100 text-center"
                  >
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${value.icon} text-2xl`}></i>
                    </div>
                    <h4 className="mt-4 font-heading text-base text-foreground-900">
                      {value.title}
                    </h4>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((item, index) => (
                    <div
                      key={item.year}
                      className={`relative flex items-start gap-6 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 -translate-x-1/2 mt-1.5 z-10"></div>
                      <div className={`pl-12 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-2">
                          {item.year}
                        </span>
                        <p className="text-sm text-foreground-700 leading-relaxed">{item.event}</p>
                      </div>
                      <div className="hidden md:block md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="reputation" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {testimonials.map((item) => (
                  <div
                    key={item.name}
                    className="p-6 rounded-lg bg-background-100"
                  >
                    <p className="text-sm text-foreground-700 leading-relaxed italic">
                      &ldquo;{item.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3 mt-5 pt-4 border-t border-background-200">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={item.avatar}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                        <p className="text-xs text-foreground-500">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-10">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
                <div className="space-y-6">
                  <h3 className="font-heading text-lg text-foreground-900">联系方式</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">公司地址</p>
                        <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                        <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">联系电话</p>
                        <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-3">关注我们</h4>
                    <div className="flex items-center gap-3">
                      <a
                        href={siteInfo.social.weibo}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                        aria-label="微博"
                      >
                        <i className="ri-weibo-line text-lg"></i>
                      </a>
                      <a
                        href={siteInfo.social.bilibili}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                        aria-label="B站"
                      >
                        <i className="ri-bilibili-line text-lg"></i>
                      </a>
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer">
                        <i className="ri-wechat-line text-lg"></i>
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-4">办公时间</h3>
                  <div className="p-6 rounded-lg bg-background-50">
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-600">周一至周五</span>
                        <span className="text-foreground-900 font-medium">9:00 - 18:00</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-600">周六</span>
                        <span className="text-foreground-900 font-medium">10:00 - 16:00</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-600">周日</span>
                        <span className="text-foreground-500">休息</span>
                      </div>
                    </div>
                    <p className="mt-4 pt-4 border-t border-background-200 text-xs text-foreground-500">
                      法定节假日休息，紧急事务请通过邮件联系
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}